import {
  Bell,
  Cart,
  Config,
  EmoBriefcase,
  EmoCart,
  EmoChat,
  EmoEye,
  EmoGift,
  EmoLocation,
  EmoMate,
  EmoMega,
  EmoMic,
  EmoPin,
  EmoPopcorn,
  EmoRacha,
  EmoRachaActive,
  Heart,
  HeartFilled,
  Menu,
  Messages,
  Pack,
  Progress,
  Share,
  User as UserIcon,
} from "@assets"
import { Link, router, usePage } from "@inertiajs/react"
import {
  ArrowLeft,
  Edit,
  LayoutDashboard,
  LogOut,
  Moon,
  Shield,
  Sun,
  Trash2,
} from "lucide-react"
import type { ReactNode } from "react"
import { createContext, useContext, useEffect, useState } from "react"

import AppLogo from "@/components/app-logo"
import { AuthButton } from "@/components/auth-button"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { UserInfo } from "@/components/user-info"
import { useInitials } from "@/hooks/use-initials"
import { useMobileNavigation } from "@/hooks/use-mobile-navigation"
import { useNotifications } from "@/hooks/use-notifications"
import { cn } from "@/lib/utils"
// Dashboard path removed - using root path instead
import {
  announcementsPath,
  businessOrdersPath,
  cartPath,
  chatGroupsPath,
  eventsPath,
  favoritesPath,
  feedPath,
  forumPath,
  interviewsPath,
  jobsPath,
  marketplacePath,
  myNeighborhoodPath,
  notificationsPath,
  ordersPath,
  rafflesPath,
  rootPath,
  sessionPath,
  settingsLocationPath,
  settingsProfilePath,
  userPath,
} from "@/routes"
import type { NavItem, SharedData, User } from "@/types"

const navItems: NavItem[] = [
  {
    title: "Inicio",
    href: feedPath(),
    icon: EmoMate,
  },
  {
    title: "Eventos",
    href: eventsPath(),
    icon: EmoPin,
  },
  {
    title: "Clips",
    href: feedPath(),
    icon: EmoPopcorn,
  },
  {
    title: "Sorteos",
    href: rafflesPath(),
    icon: EmoGift,
  },
  {
    title: "Entrevistas",
    href: interviewsPath(),
    icon: EmoMic,
  },
  {
    title: "Curros",
    href: jobsPath(),
    icon: EmoBriefcase,
  },
  {
    title: "Foro",
    href: forumPath(),
    icon: EmoEye,
  },
  {
    title: "Mercado",
    href: marketplacePath(),
    icon: EmoCart,
  },
  {
    title: "Anuncios",
    href: announcementsPath(),
    icon: EmoMega,
  },
]

interface MobileHeaderContextProps {
  showBackButton?: boolean
  showShareButton?: boolean
  showFavoriteButton?: boolean
  onBack?: () => void
  onShare?: () => void
  onFavorite?: (isFavorite: boolean) => void
  isFavorite?: boolean
}

const MobileHeaderContext = createContext<MobileHeaderContextProps>({})

export function useMobileHeader() {
  return useContext(MobileHeaderContext)
}

interface MobileHeaderProps {
  title?: string
  showDefaultRightActions?: boolean
  showBackButton?: boolean
  showShareButton?: boolean
  showFavoriteButton?: boolean
  showEditButton?: boolean
  showDeleteButton?: boolean
  onBack?: () => void
  onShare?: () => void
  onFavorite?: (isFavorite: boolean) => void
  onEdit?: () => void
  onDelete?: () => void
  onCreateChat?: () => void
  isFavorite?: boolean
  favoritesCount?: number
  children?: ReactNode
}

export default function MobileHeader({
  title,
  showBackButton = false,
  showShareButton = false,
  showFavoriteButton = false,
  showEditButton = false,
  showDeleteButton = false,
  onBack = () => window.history.back(),
  onShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: document.title,
          url: window.location.href,
        })
        .catch(console.error)
    } else {
      void navigator.clipboard.writeText(window.location.href)
    }
  },
  onFavorite = () => undefined,
  onEdit = () => undefined,
  onDelete = () => undefined,
  // onCreateChat is accepted but not currently used
  onCreateChat: _onCreateChat = () => undefined, // eslint-disable-line @typescript-eslint/no-unused-vars
  isFavorite = false,
  favoritesCount: itemFavoritesCount,
  children,
}: MobileHeaderProps) {
  const page = usePage<SharedData>()
  const { auth, app } = page.props
  interface AppProps {
    app?: { has_products?: boolean }
  }
  const hasProducts = Boolean(
    (page.props as SharedData & AppProps).app?.has_products,
  )
  const getInitials = useInitials()
  // Initialize cart count: prefer sessionStorage (survives back/forward nav), fallback to server props (correct on hard refresh)
  const [cartCount, setCartCount] = useState<number>(() => {
    const stored = sessionStorage.getItem("cart_count")
    if (stored !== null) return parseInt(stored, 10)
    const propsCount = (page.props as SharedData).cart?.count ?? 0
    sessionStorage.setItem("cart_count", String(propsCount))
    return propsCount
  })
  const neighborhoods = app?.neighborhoods ?? []
  const currentUser: User | null = auth?.user ?? null
  const profileCompletion = Math.max(
    0,
    Math.min(100, currentUser?.profile_completion_percentage ?? 0),
  )
  const streakDays = Math.max(0, currentUser?.streak_count ?? 0)
  const userPoints = Math.max(0, currentUser?.points ?? 0)
  const { unreadCount } = useNotifications()
  const messagesUnreadCount =
    (page.props as SharedData).auth?.messages?.unread_count ?? 0
  const eventsThisWeek = app?.counters?.events_this_week ?? 0
  const activeRaffles = app?.counters?.active_raffles ?? 0
  const favoritesCount = auth?.user?.favorites_count ?? 0
  const [isOpen, setIsOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [showNeighborhoodSelector, setShowNeighborhoodSelector] =
    useState(false)
  const initialNeighborhoodName: string | null =
    auth?.user?.neighborhood?.name ?? null
  const [selectedNeighborhoodName, setSelectedNeighborhoodName] = useState<
    string | null
  >(initialNeighborhoodName)
  const cleanup = useMobileNavigation()
  const userLevel = currentUser?.level ?? "Vecino Activo"
  const [isDark, setIsDark] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false,
  )

  const toggleTheme = () => {
    const dark = document.documentElement.classList.toggle("dark")
    setIsDark(dark)
    localStorage.appearance = dark ? "dark" : "light"
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", dark ? "#121212" : "#ecf4ff")
    document.documentElement.style.background = dark ? "#121212" : "#ecf4ff"
  }

  // Listen for custom cart:updated events for real-time updates
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ cart_count?: number }>
      if (typeof ce.detail?.cart_count === "number") {
        setCartCount(ce.detail.cart_count)
        sessionStorage.setItem("cart_count", String(ce.detail.cart_count))
      }
    }
    window.addEventListener("cart:updated", handler)
    return () => {
      window.removeEventListener("cart:updated", handler)
    }
  }, [])

  const getCounter = (title: string) => {
    if (title === "Eventos" && eventsThisWeek > 0)
      return `${eventsThisWeek} esta semana`
    if (title === "Sorteos" && activeRaffles > 0)
      return `${activeRaffles} activos`
    return null
  }
  const contextValue = {
    showBackButton,
    showShareButton,
    showFavoriteButton,
    showEditButton,
    showDeleteButton,
    onBack,
    onShare,
    onFavorite,
    onEdit,
    onDelete,
    isFavorite,
  }

  const streakIcon = (
    <div
      className={`text-primary hover:bg-primary/10 relative h-[32px] w-[32px] p-0.5 ${streakDays > 0 ? "bg-card rounded-full" : ""}`}
    >
      <img
        src={streakDays > 0 ? EmoRachaActive : EmoRacha}
        className="h-full w-full"
      />
    </div>
  )

  return (
    <MobileHeaderContext.Provider value={contextValue}>
      <div className="bg-background pt-safe sticky top-0 z-40 items-center backdrop-blur-sm md:hidden">
        <div className="top-[2px] flex h-[48px] items-end justify-between px-[7px]">
          <div className="flex items-center">
            {showBackButton ? (
              <Button
                variant="ghost"
                size="icon"
                className="text-primary hover:bg-primary/10 -ml-2 h-9 w-9"
                onClick={onBack}
              >
                <ArrowLeft className="text-primary h-5 w-5" />
                <span className="sr-only">Volver</span>
              </Button>
            ) : (
              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-primary hover:bg-primary/10 -ml-1 h-9 w-9"
                  >
                    <Menu className="text-primary size-6" />
                    <span className="sr-only">Abrir menú</span>
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="left"
                  className="bg-background flex h-full w-full flex-col p-0"
                >
                  <SheetHeader className="ml-8 h-[48px] pt-[10px] pb-0">
                    <SheetTitle className="flex items-center justify-start">
                      <AppLogo />
                    </SheetTitle>
                  </SheetHeader>
                  <div className="flex flex-1 flex-col overflow-y-auto px-4 pb-6">
                    {/* Navegación principal */}
                    {currentUser && (
                      <div className="mb-3">
                        <UserInfo
                          user={currentUser}
                          showEmail={false}
                          ref={userPath(currentUser.id)}
                        />

                        <div className="mt-3 space-y-2">
                          <Link
                            href={settingsProfilePath()}
                            prefetch
                            onClick={() => {
                              cleanup()
                              setIsOpen(false)
                            }}
                            className="block w-full text-left"
                          >
                            {/* Profile Progress */}
                            <div className="flex items-center gap-2 py-2 text-[13px] tracking-[0.2px]">
                              <Progress
                                className={`h-[24px] w-auto ${profileCompletion === 100 ? "fill-active" : "fill-star"}`}
                              />
                              <span className="text-muted-foreground">
                                Progreso de perfil{" "}
                                <span
                                  className={`font-semibold ${profileCompletion === 100 ? "text-active" : "text-star"}`}
                                >
                                  {profileCompletion}%
                                </span>
                              </span>
                            </div>
                            <div className="bg-modal h-[10px] w-full overflow-hidden rounded-full">
                              <div
                                className={`h-full rounded-full transition-all ${profileCompletion === 100 ? "bg-active" : "bg-star"}`}
                                style={{ width: `${profileCompletion}%` }}
                              />
                            </div>
                            {profileCompletion < 100 && (
                              <p className="text-muted-foreground mt-1 text-[11px]">
                                Completá tu perfil para +50 pts
                              </p>
                            )}
                          </Link>
                          <div className="mt-2 grid grid-cols-3 gap-2 text-center">
                            <div className="bg-card rounded-md px-2 py-1.5">
                              <div className="text-muted-foreground text-[11px]">
                                Racha
                              </div>
                              <div className="text-primary text-[13px] font-semibold">
                                {streakDays} días
                              </div>
                            </div>
                            <div className="bg-card rounded-md px-2 py-1.5">
                              <div className="text-muted-foreground text-[11px]">
                                Puntos
                              </div>
                              <div className="text-primary text-[13px] font-semibold">
                                {userPoints.toLocaleString("es-ES")}
                              </div>
                            </div>
                            <div className="bg-card rounded-md px-2 py-1.5">
                              <div className="text-muted-foreground text-[11px]">
                                Nivel
                              </div>
                              <div className="text-primary text-[13px] font-semibold">
                                {userLevel}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <nav className="grid grid-cols-2 gap-3">
                      {navItems.map((item, index) => {
                        const counter = getCounter(item.title)
                        return (
                          <Link
                            key={index}
                            href={item.href}
                            prefetch="hover"
                            onClick={() => {
                              cleanup()
                              setIsOpen(false)
                            }}
                            className={cn(
                              "flex h-[90px] flex-col items-center justify-center rounded-xl text-sm font-medium transition-colors",
                              "hover:bg-secondary/70 bg-card text-white",
                            )}
                          >
                            <span className="bg-background flex h-[44px] w-[44px] items-center justify-center rounded-full p-1 text-xl">
                              <img src={item.icon as string} className="" />
                            </span>
                            <span className="text-muted-foreground mt-[4px] text-center">
                              <span className="block text-[14px] font-semibold tracking-[0.28px]">
                                {item.title}
                              </span>
                              {counter && (
                                <span className="block text-[12px] font-medium tracking-[0.18px]">
                                  ({counter})
                                </span>
                              )}
                            </span>
                          </Link>
                        )
                      })}
                    </nav>
                    <div className="relative mt-[16px]">
                      <button
                        type="button"
                        className="bg-card-foreground flex h-[65px] w-full items-center justify-center gap-3 rounded-lg p-3"
                        onClick={() =>
                          setShowNeighborhoodSelector((prev) => !prev)
                        }
                      >
                        <span className="bg-background flex h-[44px] w-[44px] flex-shrink-0 items-center justify-center rounded-full text-base text-white">
                          <img src={EmoLocation} className="" />
                        </span>
                        <div className="flex flex-col items-start">
                          <div className="text-[12px] font-normal tracking-[0.23px] text-white dark:text-background">
                            TU UBICACIÓN
                          </div>
                          <span className="text-left text-[14px] font-normal tracking-[0.28px] text-white dark:text-background">
                            {(selectedNeighborhoodName ??
                            auth?.user?.neighborhood?.name)
                              ? `${selectedNeighborhoodName ?? auth?.user?.neighborhood?.name ?? ""}, Madrid`
                              : "Selecciona tu barrio en Madrid"}
                          </span>
                        </div>
                      </button>
                      {showNeighborhoodSelector && neighborhoods.length > 0 && (
                        <div
                          className="border-muted bg-card absolute right-0 bottom-full left-0 z-10 mb-2 max-h-56 overflow-y-auto overscroll-contain rounded-lg border shadow-lg"
                          onWheel={(e) => e.stopPropagation()}
                          onTouchMove={(e) => e.stopPropagation()}
                        >
                          {neighborhoods.map((n) => (
                            <button
                              key={n.id}
                              type="button"
                              className="text-foreground hover:bg-muted/80 w-full px-3 py-2 text-left text-sm"
                              onClick={() => {
                                setShowNeighborhoodSelector(false)
                                setSelectedNeighborhoodName(n.name)

                                router.patch(
                                  settingsLocationPath(),
                                  { neighborhood_id: n.id },
                                  { preserveState: true, preserveScroll: true },
                                )
                              }}
                            >
                              {n.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="mt-6">
                      {auth?.user && (
                        <div className="space-y-1">
                          <div className="text-muted-foreground mb-1 px-1 text-xs">
                            Tu cuenta
                          </div>
                          <Link
                            href={myNeighborhoodPath()}
                            prefetch
                            onClick={() => {
                              cleanup()
                              setIsOpen(false)
                            }}
                            className="text-muted-foreground hover:text-foreground hover:bg-muted/50 flex items-center rounded-lg px-4 py-3 text-sm font-medium"
                          >
                            <img src={EmoLocation} className="mr-2 size-5" />
                            Mi Barrio
                          </Link>
                          <Link
                            href={favoritesPath()}
                            prefetch
                            onClick={() => {
                              cleanup()
                              setIsOpen(false)
                            }}
                            className="text-muted-foreground hover:text-foreground hover:bg-muted/50 flex items-center justify-between rounded-lg px-4 py-3 text-sm font-medium"
                          >
                            <div className="flex items-center gap-2">
                              <HeartFilled className="text-destructive size-4 fill-current" />
                              Guardados
                              {favoritesCount > 0 && (
                                <span className="text-xs font-semibold">
                                  ({favoritesCount})
                                </span>
                              )}
                            </div>
                          </Link>
                          <Link
                            href={ordersPath()}
                            prefetch
                            onClick={() => {
                              cleanup()
                              setIsOpen(false)
                            }}
                            className="text-muted-foreground hover:text-foreground hover:bg-muted/50 flex items-center rounded-lg px-4 py-3 text-sm font-medium"
                          >
                            <Pack className="mr-2 h-4 w-4" />
                            Mis Pedidos
                          </Link>
                          {auth?.user?.has_business && (
                            <Link
                              href={businessOrdersPath()}
                              prefetch
                              onClick={() => {
                                cleanup()
                                setIsOpen(false)
                              }}
                              className="text-muted-foreground hover:text-foreground hover:bg-muted/50 flex items-center rounded-lg px-4 py-3 text-sm font-medium"
                            >
                              <Pack className="mr-2 h-4 w-4" />
                              Pedidos de mi negocio
                            </Link>
                          )}
                          <Link
                            href={notificationsPath()}
                            prefetch
                            onClick={() => {
                              cleanup()
                              setIsOpen(false)
                            }}
                            className="text-muted-foreground hover:text-foreground hover:bg-muted/50 flex items-center justify-between rounded-lg px-4 py-3 text-sm font-medium"
                          >
                            <div className="flex items-center gap-2">
                              <Bell className="h-4 w-4" />
                              Notificaciones
                              {unreadCount > 0 && (
                                <span className="text-xs font-semibold">
                                  ({unreadCount})
                                </span>
                              )}
                            </div>
                          </Link>
                          {auth?.user?.is_admin && (
                            <>
                              <a
                                href="/admin"
                                onClick={() => {
                                  cleanup()
                                  setIsOpen(false)
                                }}
                                className="text-muted-foreground hover:text-foreground hover:bg-muted/50 flex items-center rounded-lg px-4 py-3 text-sm font-medium"
                              >
                                <LayoutDashboard className="mr-2 h-4 w-4" />
                                Panel Admin
                              </a>
                              <a
                                href="/madmin"
                                onClick={() => {
                                  cleanup()
                                  setIsOpen(false)
                                }}
                                className="text-muted-foreground hover:text-foreground hover:bg-muted/50 flex items-center rounded-lg px-4 py-3 text-sm font-medium"
                              >
                                <Shield className="mr-2 h-4 w-4" />
                                Moderación (Madmin)
                              </a>
                            </>
                          )}
                          <button
                            type="button"
                            onClick={toggleTheme}
                            className="text-muted-foreground hover:text-foreground hover:bg-muted/50 flex w-full items-center rounded-lg px-4 py-3 text-sm font-medium"
                          >
                            {isDark ? (
                              <Sun className="mr-2 h-4 w-4" />
                            ) : (
                              <Moon className="mr-2 h-4 w-4" />
                            )}
                            {isDark ? "Modo claro" : "Modo oscuro"}
                          </button>
                          <Link
                            href={settingsProfilePath()}
                            prefetch
                            onClick={() => {
                              cleanup()
                              setIsOpen(false)
                            }}
                            className="text-muted-foreground hover:text-foreground hover:bg-muted/50 flex items-center rounded-lg px-4 py-3 text-sm font-medium"
                          >
                            <Config className="mr-2 h-4 w-4" />
                            Configuración
                          </Link>
                          <Link
                            method="delete"
                            href={sessionPath(auth.session.id)}
                            as="button"
                            onClick={() => {
                              cleanup()
                              setIsOpen(false)
                            }}
                            className="text-muted-foreground hover:text-foreground hover:bg-muted/50 flex w-full items-center rounded-lg px-4 py-3 text-sm font-medium"
                          >
                            <LogOut className="mr-2 h-4 w-4" />
                            Cerrar sesión
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            )}
          </div>

          {/* Title - Alineado a la izquierda */}
          <div className="-mb-1 ml-0 min-w-0 flex-1">
            {title && title !== "Mensajes" ? (
              <h1 className="text-primary truncate px-2 text-left text-lg font-semibold">
                {title}
              </h1>
            ) : (
              <>
                {title === "Mensajes" ? (
                  <div className="flex w-full items-center self-center">
                    {streakIcon}

                    <div className="flex flex-1 items-center justify-center">
                      <div className="ml-2 flex items-center justify-center gap-[8px]">
                        <div className="bg-card flex h-[42px] w-[42px] items-center justify-center rounded-full">
                          <img
                            src={EmoChat}
                            className="fill-chart-5 h-full w-full"
                          />
                        </div>
                        <span className="text-primary h-[31px] text-[21px] font-bold tracking-[0.21px]">
                          {title}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="inline-flex items-center">
                    {streakIcon}
                    <Link href={rootPath()}>
                      <AppLogo />
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex items-center space-x-1">
            {title !== "Mensajes" && (
              //   <Button
              //     size="icon"
              //     className="h-[34px] w-[34px] bg-card rounded-[8px] p-5px m shadow-[0px_0px_4px_#00000029]"
              //     onClick={onCreateChat}
              //   >
              //     <Foro className="size-6 text-primary" />
              //     <span className="sr-only">Nuevo chat</span>
              //   </Button>
              // ) : (
              <>
                {showShareButton && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-primary hover:bg-primary/10 h-9 w-9"
                    onClick={onShare}
                  >
                    <Share className="h-5 w-5" />
                    <span className="sr-only">Compartir</span>
                  </Button>
                )}

                {showFavoriteButton && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-primary hover:bg-primary/10 h-9 px-2"
                    onClick={() => onFavorite(!isFavorite)}
                  >
                    <span className="inline-flex items-center">
                      {isFavorite ? (
                        <HeartFilled className="text-destructive h-4 w-4 fill-current" />
                      ) : (
                        <Heart className="h-4 w-4" />
                      )}
                      {itemFavoritesCount !== undefined &&
                        itemFavoritesCount > 0 && (
                          <span className="ml-1 text-xs">
                            {itemFavoritesCount}
                          </span>
                        )}
                    </span>
                    <span className="sr-only">
                      {isFavorite
                        ? "Quitar de favoritos"
                        : "Agregar a favoritos"}
                    </span>
                  </Button>
                )}
                {showEditButton && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-primary hover:bg-primary/10 h-9 w-9"
                    onClick={onEdit}
                  >
                    <Edit className="h-4 w-4" />
                    <span className="sr-only">Editar</span>
                  </Button>
                )}

                {showDeleteButton && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:bg-destructive/10 h-9 w-9"
                    onClick={onDelete}
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="sr-only">Eliminar</span>
                  </Button>
                )}
                {!showBackButton && !showEditButton && !showDeleteButton && (
                  <>
                    {currentUser ? (
                      <div className="flex h-[42px] items-center gap-[9px]">
                        {hasProducts && cartCount > 0 && (
                          <Link
                            href={cartPath()}
                            className={`text-primary hover:bg-primary/10 relative h-[32px] w-[32px] p-[4px] ${cartCount > 0 ? "bg-card rounded-full" : ""}`}
                            prefetch
                          >
                            <Cart className="fill-ring h-full w-full" />
                            {cartCount > 0 && (
                              <span className="bg-secondary absolute -top-2.5 -right-2 inline-flex h-[24px] w-[24px] items-center justify-center rounded-full text-[12px] font-semibold text-white">
                                {cartCount}
                              </span>
                            )}
                          </Link>
                        )}

                        {/* Messages */}
                        <Link
                          href={chatGroupsPath()}
                          className={`text-primary hover:bg-primary/10 relative h-[32px] w-[32px] p-[4px] ${messagesUnreadCount > 0 ? "bg-card rounded-full" : ""}`}
                          prefetch
                        >
                          <Messages className="fill-ring h-full w-full" />
                          {messagesUnreadCount > 0 && (
                            <span className="bg-secondary absolute -top-2.5 -right-2 inline-flex h-[24px] w-[24px] items-center justify-center rounded-full text-[12px] font-semibold text-white">
                              {messagesUnreadCount > 9
                                ? "9+"
                                : messagesUnreadCount}
                            </span>
                          )}
                        </Link>
                        {/* Bell notifications */}
                        <Link
                          href={notificationsPath()}
                          className={`text-primary hover:bg-primary/10 relative h-[32px] w-[32px] p-[4px] ${unreadCount > 0 ? "bg-card rounded-full" : ""}`}
                          prefetch
                        >
                          <Bell className="fill-ring h-full w-full" />
                          {unreadCount > 0 && (
                            <span className="bg-secondary absolute -top-2.5 -right-2 inline-flex h-[24px] w-[24px] items-center justify-center rounded-full text-[12px] font-semibold text-white">
                              {unreadCount > 9 ? "9+" : unreadCount}
                            </span>
                          )}
                        </Link>

                        {/* Avatar dropdown */}
                        <DropdownMenu
                          open={isDropdownOpen}
                          onOpenChange={setIsDropdownOpen}
                        >
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              className={`h-[48px] w-[48px] rounded-full bg-transparent p-0 focus:ring-0 focus:outline-none focus-visible:border-0 focus-visible:ring-0 ${isDropdownOpen ? "bg-primary" : ""}`}
                            >
                              <Avatar
                                className={`gradient-avatar h-[42px] w-[42px] border-[2px] ${isDropdownOpen ? "border-white" : "border-chart-2"}`}
                              >
                                <AvatarImage
                                  src={currentUser.avatar_url ?? undefined}
                                  alt={currentUser.name ?? ""}
                                />
                                <AvatarFallback className="text-primary gradient-avatar p-1 text-[20px] font-semibold tracking-[0.2px]">
                                  {getInitials(currentUser.name ?? "")}
                                </AvatarFallback>
                              </Avatar>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            className="bg-card w-[266px] rounded-[15px] border-0 p-[10px]"
                            style={{ boxShadow: "0px 0px 12px #00000033" }}
                            align="end"
                          >
                            {/* User Info */}
                            <UserInfo
                              user={currentUser}
                              showNeighborhood={true}
                            />
                            <div className="px-[10px]">
                              {/* Streak Status */}
                              <div className="flex items-center gap-2">
                                <img
                                  src={
                                    streakDays > 0 ? EmoRachaActive : EmoRacha
                                  }
                                  className="-ml-1 h-[33px] w-auto"
                                />
                                <span className="text-muted-foreground text-[13px] tracking-[0.2px]">
                                  {streakDays > 0 ? (
                                    <>
                                      Racha actual{" "}
                                      <span className="text-secondary text-sm font-semibold">
                                        {streakDays}
                                      </span>
                                    </>
                                  ) : (
                                    "Sin racha actual"
                                  )}
                                </span>
                              </div>

                              {/* Profile Progress */}
                              <div className="flex items-center gap-2 py-2 text-[13px] tracking-[0.2px]">
                                <Progress
                                  className={`h-[24px] w-auto ${profileCompletion === 100 ? "fill-active" : "fill-star"}`}
                                />
                                <span className="text-muted-foreground">
                                  Progreso de perfil{" "}
                                  <span
                                    className={`font-semibold ${profileCompletion === 100 ? "text-active" : "text-star"}`}
                                  >
                                    {profileCompletion}%
                                  </span>
                                </span>
                              </div>
                              <div className="bg-modal h-[10px] w-full overflow-hidden rounded-full">
                                <div
                                  className={`h-full rounded-full transition-all ${profileCompletion === 100 ? "bg-active" : "bg-star"}`}
                                  style={{ width: `${profileCompletion}%` }}
                                />
                              </div>

                              <DropdownMenuSeparator className="bg-chart-4 mt-[16px] mb-[8px]" />

                              {/* Menu Items */}
                              <DropdownMenuItem asChild className="px-0">
                                <Link
                                  href={userPath(currentUser.id)}
                                  prefetch
                                  className="flex w-full items-center gap-2"
                                >
                                  <UserIcon className="fill-primary -ml-0.5 size-6" />
                                  <span className="text-chart-3 text-[12px] font-medium tracking-[0.18px]">
                                    Ver perfíl
                                  </span>
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild className="px-0">
                                <Link
                                  href={settingsProfilePath()}
                                  prefetch
                                  className="flex w-full items-center gap-2"
                                >
                                  <Config className="fill-primary size-5.5" />
                                  <span className="text-chart-3 text-[12px] font-medium tracking-[0.18px]">
                                    Configuración
                                  </span>
                                </Link>
                              </DropdownMenuItem>
                            </div>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={toggleTheme}
                          className="text-muted-foreground hover:text-foreground h-9 w-9 rounded-md p-1.5"
                          aria-label={isDark ? "Modo claro" : "Modo oscuro"}
                        >
                          {isDark ? (
                            <Sun className="h-full w-full" />
                          ) : (
                            <Moon className="h-full w-full" />
                          )}
                        </button>
                        <AuthButton
                          variant="ghost"
                          size="sm"
                          className="text-primary hover:bg-primary-foreground/10"
                        >
                          Iniciar sesión
                        </AuthButton>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
        {children}
      </div>
    </MobileHeaderContext.Provider>
  )
}
